import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
} from 'firebase/firestore'

import { db } from 'utils/firebase'
import { createPartialUser } from 'utils/user/createPartialUser'

import { Data } from 'types/query'
import { Subscription } from 'types/subscription'

import { formatDocSnapshot, formatQuerySnapshot } from './formatFirestoreResults'
import { fetchGroupByUserId } from './groups'
import { fetchUserByUserId } from './users'

export const fetchProgramSubscription = async (data: Data): Promise<Subscription | null> => {
  const { programId } = data

  if (!programId) return null

  const q = query(collection(db, 'subscriptions'), where('program.id', '==', programId))
  const querySnapshot = await getDocs(q)
  const results = formatQuerySnapshot(querySnapshot)
  if (results.length === 0) return null
  return results[0]
}

export const fetchSubscription = async (data: Data): Promise<Subscription | null> => {
  const { userId, programId } = data

  if (!userId || !programId) return null

  const q = query(
    collection(db, 'subscriptions'),
    where('user.id', '==', userId),
    where('program.id', '==', programId),
  )
  const querySnapshot = await getDocs(q)
  const results = formatQuerySnapshot(querySnapshot)
  if (results.length === 0) return null
  return results[0]
}

export const fetchSubscriptionFromTrainerId = async (data: Data) => {
  const { trainerId, userId } = data
  const program = await fetchGroupByUserId({ userId: trainerId })
  if (!program?.id) return null

  const subscription = await fetchSubscription({
    userId,
    programId: program?.id,
  })

  return subscription
}

export const fetchSubscriptionsByUserId = async (data: Data): Promise<Array<Subscription>> => {
  if (!data.id) return []
  const q = query(collection(db, 'subscriptions'), where('user.id', '==', data.id))
  const querySnapshot = await getDocs(q)
  let subscriptions = formatQuerySnapshot(querySnapshot)

  const uniqueIds: string[] = []
  subscriptions = subscriptions?.filter((sub) => {
    const programId = sub.program.id as string
    if (!uniqueIds.includes(programId)) {
      uniqueIds.push(programId)
      return true
    }
    return false
  })

  return subscriptions
}

export const deleteSubscription = async (data: Data) => {
  const { subscriptionId } = data
  const subscriptionRef = doc(db, 'subscriptions', subscriptionId)
  await deleteDoc(subscriptionRef)
}

export const postSubscription = async (
  data: Data,
): Promise<{ subscription: Subscription | null; created: boolean }> => {
  const { user, trainerId } = data
  const userId = user?.id

  if (!trainerId) return { subscription: null, created: false }
  const program = await fetchGroupByUserId({ userId: trainerId })
  const trainer = await fetchUserByUserId({ userId: trainerId })

  if (!program || !program?.id || !trainer) {
    return { subscription: null, created: false }
  }

  const subscription = await fetchSubscription({
    userId,
    programId: program?.id,
  })
  if (subscription) {
    return { subscription, created: false }
  }

  const newSubscriptionRef = await addDoc(collection(db, 'subscriptions'), {
    program: {
      ...program,
      trainer: { id: trainer.id },
    },
    user: createPartialUser(user),
  })
  const newSubscription = await getDoc(newSubscriptionRef)
  return {
    subscription: formatDocSnapshot(newSubscription) as Subscription,
    created: true,
  }
}

export const fetchSubscriptionsByTrainerId = async (data: Data) => {
  if (!data || !data?.trainerId)
    throw new Error('No data passed into fetchSubscriptionsByTrainerId')

  const { trainerId } = data

  // Step 1: Fetch the trainers programs and select the first one (Later, we might support more)
  const trainersProgramsQuery = query(
    collection(db, 'programs'),
    where('trainer.id', '==', trainerId),
  )

  const trainersProgramsSnapshot = await getDocs(trainersProgramsQuery)
  const trainersPrograms = formatQuerySnapshot(trainersProgramsSnapshot)

  const keyTrainerProgram = trainersPrograms && trainersPrograms.length && trainersPrograms[0]
  const keyTrainerProgramId = keyTrainerProgram && keyTrainerProgram.id

  // Step 2: Fetch the users that are subscribed to the trainer's program
  const subscriptionsQuery = query(
    collection(db, 'subscriptions'),
    where('program.id', '==', keyTrainerProgramId),
  )
  const subscriptionsSnapshot = await getDocs(subscriptionsQuery)
  return formatQuerySnapshot(subscriptionsSnapshot)
}

export const fetchSubscriptionsByProgramId = async (data: Data) => {
  if (!data || !data?.programId)
    throw new Error('No data passed into fetchSubscriptionsByProgramId')

  const { programId } = data

  const subscriptionsQuery = query(
    collection(db, 'subscriptions'),
    where('program.id', '==', programId),
  )
  const subscriptionsSnapshot = await getDocs(subscriptionsQuery)
  return formatQuerySnapshot(subscriptionsSnapshot)
}

export const cancelSubscriptionByUserIdAndProgramId = async (userId: string, programId: string) => {
  const subscriptionsQuery = query(
    collection(db, 'subscriptions'),
    where('user.id', '==', userId),
    where('program.id', '==', programId),
  )

  const subscriptionsSnapshot = await getDocs(subscriptionsQuery)
  const subscriptions = formatQuerySnapshot(subscriptionsSnapshot)
  const subscription = subscriptions && subscriptions.length && subscriptions[0]

  // eslint-disable-next-line no-underscore-dangle
  if (subscription._original !== null) {
    throw new Error('Unable to modify Stripe managed subscriptions.')
  }

  const subscriptionRef = doc(db, 'subscriptions', subscription.id)

  await setDoc(subscriptionRef, { status: 'canceled' }, { merge: true })

  return getDoc(subscriptionRef)
}
